import { Button } from '@/components/form/Button';
import { buttonClassName, ButtonVariant } from '@/components/form/buttonHelper';
import { FileInput } from '@/components/form/fields/file/FileInput';
import { Step } from '@/components/multistep/Questionnaire';
import { useControls } from '@/components/multistep/useMultistep';
import { Card } from '@/components/sections/Card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/table/Table';
import SiDownload from '@/icons/SiDownload';
import { ParseError, ValidationError } from '@/pages/product/product/bulk/steps/IssuesStep';
import { useCreateCompanies, useCreateCompanyCode, useUpdateCompanies } from '@/services/api/hooks/companies';

import { ApprovalStatus } from '@/services/api/contracts/companies';
import { useCSV, useResult } from '@/services/csv/useCSVService';
import { FC, PropsWithChildren, useCallback, useMemo, useState } from 'react';
import { useErrorHandler } from 'react-error-boundary';
import { createTemplate } from './createTemplate';

export type CustomerCSVParser = typeof import('./CSVParsingWorker');
export type Result = Awaited<ReturnType<CustomerCSVParser['processCustomerCSV']>>;

export interface UploadStepProps {
  skipname?: string;
}

export const UploadStep: FC<PropsWithChildren<UploadStepProps>> = ({ children }) => {
  const [onComplete, canMoveOn] = useCSV(state => [state.onComplete, !!state.result]);
  const { skipToStep } = useControls();

  const template = useMemo(() => {
    const template = createTemplate();
    return URL.createObjectURL(new Blob([template], { type: 'octet/stream' }));
  }, []);

  const processAndProceed = async (file: File) => {
    // Create an instance of a worker using comlink syntactic sugar
    const instance = new ComlinkWorker<CustomerCSVParser>(new URL('./CSVParsingWorker', import.meta.url));

    // This is specific to this process using a Product validation
    // We could pass a zod schema to the worker and have this be generic but the performance hit is too great
    const result = await instance.processCustomerCSV(file);

    if (result.data.length) {
      result.errors.genericErrors = [];

      const companyNameCounts: Record<string, number> = result.data.reduce((a, e) => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        a[e.RegName] = ++a[e.RegName] || 0;
        return a;
      }, {});
      const duplicateCompanyNames = Object.keys(companyNameCounts).filter(key => companyNameCounts[key] > 0);
      if (Object.keys(duplicateCompanyNames).length > 0) {
        duplicateCompanyNames.forEach(
          name => result.errors.genericErrors?.push({ message: `Company name: ${name} already exists` })
        );
      }

      const regNumberCounts: Record<string, number> = result.data.reduce((a, e) => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        a[e.RegNumber] = ++a[e.RegNumber] || 0;
        return a;
      }, {});
      const duplicateRegNumber = Object.keys(regNumberCounts).filter(key => regNumberCounts[key] > 0);
      if (Object.keys(duplicateRegNumber).length > 0) {
        duplicateRegNumber.forEach(
          name => result.errors.genericErrors?.push({ message: `Reg number: ${name} already exists` })
        );
      }

      const invalidEmployeeCountRows = result.data.filter(item => Number.parseInt(item.NumEmployees) < 1);
      result.errors.genericErrors.push(
        ...invalidEmployeeCountRows.map(item => {
          return {
            message: `Invalid employee count for ${item.RegName}. Please ensure employee count is greater or equal to 1`
          };
        })
      );
    }

    onComplete(result);

    // If there any issues, show them
    if (
      result.errors.couldNotParse.length > 0 ||
      result.errors.couldNotValidate.length > 0 ||
      result.errors.genericErrors?.length
    ) {
      skipToStep('issues');
      return;
    }

    skipToStep('review');
  };

  const validate = useCallback(() => canMoveOn, [canMoveOn]);
  return (
    <div>
      <Step
        validate={validate}
        controls="manual"
        id="upload"
        title="Upload"
        description="Upload a spreadsheet of customers to bulk upload them to the system."
      >
        <div className="flex w-full flex-col gap-2">
          <a className={buttonClassName(ButtonVariant.Mono)} href={template} download="template.csv">
            <SiDownload />
            <span>Download template</span>
          </a>
          <FileInput
            id="csv"
            placeholder="Drop or click to upload spreadsheet"
            value={[]}
            onChange={([file]) => processAndProceed(file)}
          />
        </div>
        {children && children}
      </Step>
    </div>
  );
};

export interface ReviewProps {
  mspId: string | undefined;
  nextLabel: string;
  onFinish: () => boolean;
}

export const ReviewStep: FC<ReviewProps> = ({ mspId, nextLabel, onFinish }) => {
  const { previous } = useControls();
  if (!mspId) throw Error('No mspId');
  const result = useResult<Result>();
  if (!result) throw Error('No result');

  const createCustomers = useCreateCompanies(mspId);
  const updateCustomers = useUpdateCompanies(mspId);
  const createCustomerCodes = useCreateCompanyCode();

  const [loading, setLoading] = useState(false);
  const handleError = useErrorHandler();
  const { next } = useControls();

  const submit = async () => {
    setLoading(true);
    try {
      const createCustomerCodeResult = await createCustomerCodes.mutateAsync({
        names: result.data.map(item => item.RegName)
      });

      const companiesInfo = result.data.map(item => {
        return {
          name: item.RegName,
          code: createCustomerCodeResult.find(code => code.name === item.RegName)?.code || '',
          registeredInfo: {
            name: item.RegName,
            number: item.RegNumber,
            address: `${item.RegAddress1 ?? ''}|${item.RegAddress2 ?? ''}|${item.RegAddressCity ?? ''}|${
              item.RegAddressCounty ?? ''
            }|${item.RegPostCode ?? ''}|${item.RegCountry ?? ''}`,
            employeeCount: parseInt(item.NumEmployees, 10),
            vatNumber: item.RegVatNumber
          }
        };
      });

      const createResponse = await createCustomers.mutateAsync({
        companiesInfo: companiesInfo
      });

      const updatePayload = createResponse.items.map(item => {
        return {
          name: item.name,
          code: item.code,
          registeredInfo: item.registeredInfo,
          id: item.id,
          approvalStatus: ApprovalStatus.Full,
          disabled: false
        };
      });

      await updateCustomers.mutateAsync({
        companiesInfo: updatePayload
      });

      if (!onFinish()) {
        await next();
      }
    } catch (ex) {
      handleError(ex);
    }
    setLoading(false);
  };

  return (
    <div className="flex flex-col gap-5">
      <Card>
        <Table className="w-[1000px]">
          <TableHeader>
            <TableRow>
              <TableHead>Registered Name</TableHead>
              <TableHead>Address 1</TableHead>
              <TableHead>Address 2</TableHead>
              <TableHead>Town/City</TableHead>
              <TableHead>State/County</TableHead>
              <TableHead>Postal/Zip Code</TableHead>
              <TableHead>Country</TableHead>
              <TableHead>Registered Number</TableHead>
              <TableHead>Employee Count</TableHead>
              <TableHead>Vat Number</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody className="max-h-96 overflow-y-scroll">
            {result.data.map(customer => {
              return (
                <TableRow key={customer.RegNumber} className="hover:bg-muted/50">
                  <TableCell className="font-medium">{customer.RegName}</TableCell>
                  <TableCell>{customer.RegAddress1}</TableCell>
                  <TableCell>{customer.RegAddress2}</TableCell>
                  <TableCell>{customer.RegAddressCity}</TableCell>
                  <TableCell>{customer.RegAddressCounty}</TableCell>
                  <TableCell>{customer.RegPostCode}</TableCell>
                  <TableCell>{customer.RegCountry}</TableCell>
                  <TableCell>{customer.RegNumber}</TableCell>
                  <TableCell>{customer.NumEmployees}</TableCell>
                  <TableCell>{customer.RegVatNumber}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>
      <div className="flex justify-between">
        <Button onClick={previous}>Previous</Button>
        <Button loading={loading} onClick={submit}>
          {nextLabel}
        </Button>
      </div>
    </div>
  );
};

export const IssuesStep = () => {
  const result = useResult<Result>();
  const { previous, next } = useControls();
  if (!result) throw Error('No result');

  return (
    <div className="space-y-3">
      <div className="space-y-2 font-medium">
        {result.errors.genericErrors?.map((error, index) => (
          <p key={`duplicateProductError-${index}`}>{error.message}</p>
        ))}
      </div>

      {result.errors.couldNotParse.length !== 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Row</TableHead>
              <TableHead>Issue</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {result.errors.couldNotParse.map(m => (
              <ParseError key={m.index} {...m} />
            ))}
          </TableBody>
        </Table>
      )}
      {result.errors.couldNotValidate.length !== 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Row</TableHead>
              <TableHead>Column</TableHead>
              <TableHead>Issue</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {result.errors.couldNotValidate.map((m, i) => (
              <ValidationError key={i} {...m} />
            ))}
          </TableBody>
        </Table>
      )}
      <div className="flex justify-between">
        <Button onClick={previous}>Previous</Button>
        <Button disabled={Object.keys(result.errors).length > 0} onClick={() => next()}>
          Next
        </Button>
      </div>
    </div>
  );
};
